/**
 * Event log strip for the scene: collapsible, count badge, newest first,
 * clear; it expands on a new entry while live or simulated data runs (the
 * previous EventLogManager, which never had a DOM to render into).
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, ScrollText, Trash2 } from 'lucide-react';
import { Chip, IconButton, type Tone } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { useTwin, type TwinEvent } from '../../store';
import { fmtTime } from '../lib/format';

const SEVERITY_TONE: Record<TwinEvent['severity'], Tone> = { critical: 'warning', warning: 'caution', info: 'neutral' };

export function EventLog({ className }: { className?: string }) {
  const events = useTwin((s) => s.events);
  const clear = useTwin((s) => s.clearEvents);
  const liveSource = useTwin((s) => s.liveSource);
  const [collapsed, setCollapsed] = useState(true);
  const lastSeen = useRef<number>(events[0]?.id ?? 0);

  // A new entry re-expands a collapsed log, as the original did.
  useEffect(() => {
    const newest = events[0]?.id ?? 0;
    if (newest > lastSeen.current) {
      lastSeen.current = newest;
      setCollapsed(false);
    }
  }, [events]);

  if (events.length === 0 && liveSource === 'off') return null;

  return (
    <div className={cn('pointer-events-auto flex w-[360px] flex-col rounded-panel border border-hairline-strong bg-raised/90 text-xs shadow-elev-2 backdrop-blur', className)}>
      <div className="flex h-8 items-center justify-between px-2">
        <button type="button" onClick={() => setCollapsed((v) => !v)} className="flex items-center gap-2 text-ink-2 hover:text-ink" aria-expanded={!collapsed}>
          <ScrollText className="h-3.5 w-3.5" />
          <span className="text-[10px] uppercase tracking-wider">Event log</span>
          <span className="rounded-full bg-overlay px-1.5 font-mono text-[10px] text-ink">{events.length}</span>
          {collapsed ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        <IconButton label="Clear events" className="h-6 w-6" onClick={clear} disabled={events.length === 0}>
          <Trash2 className="h-3.5 w-3.5" />
        </IconButton>
      </div>
      {!collapsed && (
        <ul className="max-h-48 overflow-y-auto border-t border-hairline scrollbar-thin">
          {events.length === 0 && <li className="px-3 py-2 text-ink-3">No events yet.</li>}
          {events.map((e) => (
            <li key={e.id} className="flex items-start gap-2 border-b border-hairline px-3 py-1.5 last:border-b-0">
              <Chip tone={SEVERITY_TONE[e.severity]} className="mt-0.5 px-1.5 py-0 text-[9px] uppercase">
                {e.severity}
              </Chip>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-ink">{e.title}</span>
                  <span className="shrink-0 font-mono text-[10px] text-ink-3">{fmtTime(e.timeMs)}</span>
                </div>
                {e.message && <div className="leading-snug text-ink-2">{e.message}</div>}
                {e.equipment && <div className="font-mono text-[10px] text-ink-3">{e.equipment}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
